/**
 * Sibling Variant Radios Web Component
 * Handles variant selection for products with sibling products via metaobjects
 */

class SiblingVariantRadios extends HTMLElement {
  constructor() {
    super()
    this.siblingProducts = []
    this.handleChange = this.handleChange.bind(this)
  }

  // Initialize the component by loading data and setting up event listeners
  init() {
    this.loadSiblingProducts()
  }

  connectedCallback() {
    this.isProductCard = this.dataset.placement === 'product-card'
    this.init()
    if (!this.isProductCard) {
      this.syncCheckedColorDisabled()
    }
    this.addEventListener('change', this.handleChange)
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange)
  }

  // Load sibling products data from the component's data attribute
  loadSiblingProducts() {
    try {
      this.siblingProducts = this.dataset.siblingProducts
        ? JSON.parse(this.dataset.siblingProducts)
        : []
    } catch (error) {
      this.siblingProducts = []
    }
  }

  // Handle radio button changes - color changes load sibling products, others update current product
  async handleChange(event) {
    if (event.target.type !== 'radio') return

    if (this.isColorOption(event.target)) {
      await this.handleColorChange(event.target.value)
    } else {
      // Handle size/other options with standard variant logic
      this.handleStandardVariantChange()
      this.syncCheckedColorDisabled()
    }
  }

  // Check if the input field is for color/colour options
  isColorOption(input) {
    const fieldset = input.closest('fieldset')
    const legend = fieldset?.querySelector('legend')
    const text = legend?.textContent.toLowerCase() || ''
    return text.includes('color') || text.includes('colour')
  }

  // Handle color changes by loading the appropriate sibling product
  async handleColorChange(selectedColor) {
    const siblingProduct = this.findSiblingByColor(selectedColor)

    if (siblingProduct) {
      if (this.isProductCard) {
        this.dispatchEvent(
          new CustomEvent('product-card:reload', {
            detail: siblingProduct,
          })
        )
      } else {
        await this.loadSiblingProduct(siblingProduct)
      }
    }
  }

  // Find a sibling product that has the selected color as one of its variant options
  findSiblingByColor(color) {
    return this.siblingProducts.find((product) =>
      product.variants?.some((variant) =>
        [variant.option1, variant.option2, variant.option3].includes(color)
      )
    )
  }

  // Load sibling product section and replace entire section content
  async loadSiblingProduct(siblingProduct) {
    const handle = this.getProductHandle(siblingProduct)

    try {
      // Get current size selection and find matching variant in sibling product
      const currentSizeValue = this.getCurrentSizeValue()
      const targetVariantId = this.findMatchingVariantId(
        siblingProduct,
        currentSizeValue
      )

      // Build URL with variant parameter if we have a matching variant
      let url = `/products/${handle}?section_id=${this.dataset.section}`
      if (targetVariantId) {
        url += `&variant=${targetVariantId}`
      }

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        mode: 'no-cors',
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch sibling product: ${response.status} ${response.statusText}`
        )
      }

      const html = await response.text()
      this.replaceSection(html)

      // Update URL with the variant parameter
      if (this.dataset.updateUrl !== 'false') {
        let newUrl = `/products/${handle}`
        if (targetVariantId) {
          newUrl += `?variant=${targetVariantId}`
        }
        window.history.replaceState({}, '', newUrl)
      }
    } catch (error) {
      console.warn('Failed to load sibling product:', {
        handle,
        section: this.dataset.section,
        error: error.message,
      })
      // Keep current product - user experience remains intact
    }
  }

  // Find the variant ID in the sibling product that matches the current size selection
  findMatchingVariantId(siblingProduct, currentSizeValue) {
    if (!currentSizeValue || !siblingProduct.variants) return null

    // Find the variant that has the same size as the current selection
    const matchingVariant = siblingProduct.variants.find((variant) => {
      // Check if any of the variant options match the current size value
      return [variant.option1, variant.option2, variant.option3].includes(
        currentSizeValue
      )
    })

    return matchingVariant ? matchingVariant.id : null
  }

  // Extract the product handle from sibling product data
  getProductHandle(product) {
    if (product.handle) return product.handle
    if (product.url) {
      const match = product.url.match(/\/products\/([^/?]+)/)
      return match?.[1]
    }
    return null
  }

  // Replace the entire section with new HTML and reinitialize all components
  replaceSection(html) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const newSection = doc.querySelector(
      `[data-section="${this.dataset.section}"]`
    )
    const currentSection = document.querySelector(
      `[data-section="${this.dataset.section}"]`
    )

    if (newSection && currentSection) {
      // Replace the entire section
      currentSection.parentNode.replaceChild(newSection, currentSection)

      // Reinitialize the data-option-value event listeners
      this.reinitializeOptionValueListeners()
      document.dispatchEvent(new CustomEvent('product:reload'))
    }
  }

  // Reinitialize event listeners for data-option-value spans
  reinitializeOptionValueListeners() {
    const fieldsets = document.querySelectorAll('.js.product-form__input')

    fieldsets.forEach((fieldset) => {
      const inputs = fieldset.querySelectorAll('input[type="radio"]')

      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) {
            const legendSpan = fieldset.querySelector(
              'legend [data-option-value]'
            )
            legendSpan.textContent = input.nextElementSibling.textContent.trim()
          }
        })

        if (input.checked) {
          input.dispatchEvent(new Event('change'))
        }
      })
    })
  }

  // Handle standard variant changes (size, etc.) within the current product
  handleStandardVariantChange() {
    const variant = this.getCurrentVariant()

    if (!variant) {
      return this.toggleAddButton(true, 'Sold Out', false, false)
    }

    // Update variant input forms
    document
      .querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
      )
      .forEach((form) => {
        const input = form.querySelector('input[name="id"]')
        if (input) {
          input.value = variant.id
          input.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })

    // Update URL with variant
    if (this.dataset.updateUrl !== 'false') {
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?variant=${variant.id}`
      )
    }

    // Trigger renderProductInfo like standard VariantRadios does
    this.renderProductInfo(variant)
  }

  // Render product info (price, SKU, inventory, etc.) for the selected variant
  renderProductInfo(variant) {
    const requestedVariantId = variant.id

    this.toggleAddButton()

    fetch(
      `${window.location.pathname}?variant=${requestedVariantId}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        // Prevent unnecessary ui changes from abandoned selections
        if (this.getCurrentVariant()?.id !== requestedVariantId) return

        const html = new DOMParser().parseFromString(responseText, 'text/html')

        // Update price
        const priceSource = html.getElementById(`price-${this.dataset.section}`)
        const priceDest = document.getElementById(
          `price-${this.dataset.section}`
        )
        if (priceSource && priceDest) {
          priceDest.innerHTML = priceSource.innerHTML
          priceDest.classList.remove('visibility-hidden')
        }

        // Update SKU
        const skuSource = html.getElementById(`Sku-${this.dataset.section}`)
        const skuDest = document.getElementById(`Sku-${this.dataset.section}`)
        if (skuSource && skuDest) {
          skuDest.innerHTML = skuSource.innerHTML
          skuDest.classList.toggle(
            'visibility-hidden',
            skuSource.classList.contains('visibility-hidden')
          )
        }

        // Update inventory
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.section}`
        )
        const inventoryDest = document.getElementById(
          `Inventory-${this.dataset.section}`
        )
        if (inventorySource && inventoryDest) {
          inventoryDest.innerHTML = inventorySource.innerHTML
          inventoryDest.classList.toggle(
            'visibility-hidden',
            inventorySource.innerText === ''
          )
        }

        // Update add to cart button
        const formSource = html.getElementById(
          `product-form-${this.dataset.section}`
        )
        if (formSource) {
          const preOrderAvailable = formSource.dataset.preOrder === 'true'
          this.toggleAddButton(
            !variant.available,
            window.variantStrings?.soldOut || 'Sold Out',
            false,
            preOrderAvailable
          )
        }
      })
  }

  // Update add to cart button state
  toggleAddButton(disable = true, text, modifyClass = true, preOrder = false) {
    const productForm = document.getElementById(
      `product-form-${this.dataset.section}`
    )
    if (!productForm) return

    const addButton = productForm.querySelector('[name="add"]')
    const addButtonText = productForm.querySelector('[name="add"] > span')
    if (!addButton) return

    if (disable && !preOrder) {
      addButton.setAttribute('disabled', 'disabled')
      if (text) addButtonText.textContent = text
    } else {
      addButton.removeAttribute('disabled')
      addButtonText.textContent =
        window.variantStrings?.addToCart || 'Add to Cart'
    }
  }

  // Get the currently selected variant based on all selected options
  getCurrentVariant() {
    const variantData = this.getVariantData()
    const selectedOptions = this.getSelectedOptions()

    return variantData.find((variant) =>
      variant.options.every(
        (option, index) => selectedOptions[index] === option
      )
    )
  }

  // Get variant data from the JSON script tag
  getVariantData() {
    const script = this.querySelector('script[type="application/json"]')
    return script ? JSON.parse(script.textContent) : []
  }

  // Get currently selected values from all radio button fieldsets
  getSelectedOptions() {
    return Array.from(this.querySelectorAll('fieldset'))
      .map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked')
        return checked?.value
      })
      .filter(Boolean)
  }

  // Get the current size value selection
  getCurrentSizeValue() {
    const sizeFieldset = this.querySelector('fieldset.fieldset-size')

    const checkedInput = sizeFieldset.querySelector(
      'input[type="radio"]:checked'
    )
    return checkedInput ? checkedInput.value : null
  }

  syncCheckedColorDisabled() {
    const variant = this.getCurrentVariant()
    const isAvailable = variant && variant.available ? variant.available : false

    const checkedOptions = Array.from(
      this.querySelectorAll('input[type="radio"]:checked')
    )

    checkedOptions.forEach((option) => {
      if (isAvailable) {
        option.removeAttribute('aria-disabled')
      } else {
        option.setAttribute('aria-disabled', 'true')
      }
    })
  }
}

customElements.define('sibling-variant-radios', SiblingVariantRadios)
